import { Dispatch, SetStateAction, useEffect, useState } from "react";

import {
  EventQuery,
  MembershipQuery,
  TicketType,
  MembershipType,
} from "~graphql/sdk";

type Promotion =
  | EventQuery["event"]["multiBuyPromotions"][number]
  | MembershipQuery["membership"]["multiBuyPromotions"][number];

/** The type representing the "get" side of a multi-buy promotion (ticket type or membership type). */
type GetType =
  | Pick<
      TicketType,
      | "id"
      | "name"
      | "description"
      | "restrictions"
      | "kind"
      | "isGrouped"
      | "groupAmount"
      | "bookingFeeType"
      | "bookingFee"
    >
  | Pick<
      MembershipType,
      "id" | "name" | "description" | "restrictions" | "kind"
    >
  | null
  | undefined;

/** A single active multi-buy promotion with computed quantities. */
export interface ActivePromotion {
  promotion: Promotion;
  giftedQty: number;
  /**
   * The ticket type or membership type on the "get" side of the promotion.
   * Derived from `getTicketType` (events) or `getMembershipType` (memberships).
   * May be `null` or `undefined` if the membership promotion has no `getMembershipType`.
   */
  getType: GetType;
  /** The number of "get" seats that meet the quantity threshold. */
  getSeatQty: number;
  /** The number of "buy" seat groups that meet the `buyQuantity` threshold. */
  buySeatQty: number;
}

export interface UseNonSeatedMultibuyResult {
  activePromotions: ActivePromotion[];
  selectedPromotion: (ActivePromotion | never[])[];
  setSelectedPromotion: Dispatch<SetStateAction<(ActivePromotion | never[])[]>>;
}

export const useNonSeatedMultibuy = (
  promotions: Promotion[],
  seats: any[]
): UseNonSeatedMultibuyResult => {
  const [activePromotions, setActivePromotions] = useState<ActivePromotion[]>(
    []
  );
  const [selectedPromotion, setSelectedPromotion] = useState<
    (ActivePromotion | never[])[]
  >([activePromotions?.[0] || []]);

  useEffect(() => {
    if (promotions && seats.length > 0) {
      promotions
        .filter((promotion) => promotion.enabled)
        .forEach((promo) => {
          const getType =
            ("getTicketType" in promo && promo?.getTicketType) ||
            ("getMembershipType" in promo && promo?.getMembershipType);
          const buyType =
            ("buyTicketType" in promo && promo?.buyTicketType) ||
            ("buyMembershipType" in promo && promo?.buyMembershipType);

          const buySeats = seats?.filter(
            ({ ticketType, membershipType }) =>
              (ticketType?.value ||
                ticketType?.id ||
                membershipType?.id ||
                ticketType) === buyType.id
          );

          const getSeats = seats?.filter(
            ({ ticketType, membershipType }) =>
              (ticketType?.value ||
                ticketType?.id ||
                membershipType?.id ||
                ticketType) === getType.id
          );

          const buyTotal = buySeats?.reduce(
            (acc, curr) => acc + curr.quantity,
            0
          );

          const buySeatQty = Math.floor(buyTotal / promo.buyQuantity);

          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const getTotal = getSeats?.reduce(
            (acc, curr) => acc + curr.quantity,
            0
          );

          const getSeatQty = Math.ceil(getTotal / promo.getQuantity);

          const giftedQty =
            buySeatQty * promo.getQuantity <= getTotal
              ? buySeatQty * promo.getQuantity
              : getTotal;

          const foundPromo = activePromotions.find(
            ({ promotion }) => promotion.id === promo.id
          );

          if (getSeatQty > 0 && buySeatQty > 0) {
            if (foundPromo) {
              if (
                (foundPromo.buySeatQty !== buySeatQty &&
                  foundPromo.getSeatQty !== getSeatQty) ||
                foundPromo.giftedQty !== giftedQty
              ) {
                setActivePromotions([
                  ...(activePromotions?.filter(
                    ({ promotion }) => promotion.id !== promo.id
                  ) ?? []),
                  {
                    promotion: promo,
                    giftedQty,
                    getType,
                    getSeatQty,
                    buySeatQty,
                  },
                ]);
              }
            } else {
              setActivePromotions([
                ...activePromotions,
                {
                  promotion: promo,
                  giftedQty,
                  getType,
                  getSeatQty,
                  buySeatQty,
                },
              ]);
            }
          } else {
            if (foundPromo) {
              setActivePromotions([
                ...(activePromotions?.filter(
                  ({ promotion }) => promotion.id !== promo.id
                ) ?? []),
              ]);
            }
          }
        });
    }
  }, [promotions, seats]);

  return { activePromotions, selectedPromotion, setSelectedPromotion };
};
